import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

const columnHelper = createColumnHelper();

function MergerSubsDetailList({
  isAuthenticated,
  user,
  data,
  updateTableState,
  saveRecord,
  validateRelatedRecord,
  mergersubid,
  refreshDetails,
}) {
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const deleteIntentRowRef = useRef(null);

  const sourceRows = useMemo(
    () => (data || []).filter((row) => row.source_or_dest === "S"),
    [data]
  );
  const destinationRows = useMemo(
    () => (data || []).filter((row) => row.source_or_dest === "D"),
    [data]
  );

  const updateRows = (updater) => {
    const next = typeof updater === "function" ? updater([...(data || [])]) : updater;
    updateTableState(next || []);
  };

  const getGlobalIndexForSource = (sourceOrDest, sourceIndex) => {
    let seen = -1;
    for (let i = 0; i < (data || []).length; i += 1) {
      if (data[i].source_or_dest === sourceOrDest) {
        seen += 1;
        if (seen === sourceIndex) {
          return i;
        }
      }
    }
    return -1;
  };

  const updateDataBySource = (sourceOrDest, rowIndex, columnId, value) => {
    updateRows((rows) => {
      const globalIndex = getGlobalIndexForSource(sourceOrDest, rowIndex);
      if (globalIndex < 0) {
        return rows;
      }

      const next = [...rows];
      next[globalIndex] = {
        ...next[globalIndex],
        [columnId]: value,
        modified: true,
      };
      return next;
    });
  };

  const addMergerSubDetailRecord = (sourceOrDest) => {
    if (!mergersubid) {
      setUi({
        showAlert: true,
        errorMessage: "Select and save a merger/sub record before adding details.",
      });
      return;
    }

    const newRecord = {
      property_key: "",
      merger_sub_detail_id: "",
      merger_sub_id: mergersubid,
      source_or_dest: sourceOrDest,
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
    };

    updateRows((rows) => [...rows, newRecord]);
  };

  const resolveDetailId = (result) =>
    result?.id ??
    result?.merger_sub_detail_id ??
    result?.uid ??
    result?.mergerSubDetailId ??
    result?.data?.id ??
    result?.data?.merger_sub_detail_id ??
    null;

  const handleSave = async ({ row, column, value }) => {
    const hasChanged = row.original?.[column.id] !== value;
    const recordToSave = {
      ...row.original,
      [column.id]: value,
      modified: hasChanged ? true : row.original?.modified,
    };
    setUi((prev) => ({ ...prev, showAlert: false, errorMessage: "" }));

    if (typeof validateRelatedRecord === "function") {
      const [isValid, validationMessage] = validateRelatedRecord(recordToSave, "mergerSubDetails");
      if (!isValid) {
        setUi({ showAlert: true, errorMessage: validationMessage });
        return false;
      }
    }

    if (!saveRecord) {
      return true;
    }

    try {
      const result = await saveRecord(recordToSave, "mergerSubDetails", "merger_sub_detail_id");
      if (!result?.result) {
        setUi({
          showAlert: true,
          errorMessage: result?.error_message || "Network Error: Could not save detail record.",
        });
        return false;
      }

      updateRows((rows) =>
        rows.map((item) => {
          const isSame = item === row.original;
          if (!isSame) {
            return item;
          }

          const createdId = resolveDetailId(result);

          const next = {
            ...item,
            [column.id]: value,
            modified: false,
            new_record: false,
          };

          if (item.new_record && createdId) {
            next.merger_sub_detail_id = createdId;
          }

          return next;
        })
      );

      return { skipLocalUpdate: true };
    } catch (error) {
      console.error(error);
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save detail record." });
      return false;
    }
  };

  const handleDeleteRecord = async (row) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this record?");
    if (!confirmDelete) {
      return;
    }

    const record = row.original;
    deleteIntentRowRef.current = record;
    const hasPersistedId = Boolean(record.merger_sub_detail_id);

    // Delete handler never creates rows. If no persisted id exists, remove locally.
    if (!hasPersistedId) {
      updateRows((rows) => rows.filter((item) => item !== record));
      deleteIntentRowRef.current = null;
      return;
    }

    try {
      const result = await saveRecord(
        {
          ...record,
          merger_sub_detail_id: record.merger_sub_detail_id,
          new_record: false,
          archived_date: new Date(),
          modified: true,
        },
        "mergerSubDetails",
        "merger_sub_detail_id"
      );

      if (!result?.result) {
        setUi({ showAlert: true, errorMessage: result?.error_message || "Failed to delete detail record." });
        deleteIntentRowRef.current = null;
        return;
      }

      if (typeof refreshDetails === "function") {
        await refreshDetails();
      } else {
        updateRows((rows) => rows.filter((item) => item !== record));
      }
      deleteIntentRowRef.current = null;
    } catch (error) {
      console.error(error);
      setUi({ showAlert: true, errorMessage: "Failed to delete detail record." });
      deleteIntentRowRef.current = null;
    }
  };

  const buildColumns = (headerText, sourceOrDest) => [
    columnHelper.accessor("property_key", {
      id: "property_key",
      header: headerText,
      cell: EditableCell,
      size: 220,
    }),
    columnHelper.display({
      id: `delete_record_${sourceOrDest}`,
      header: "",
      cell: (info) => (
        <Button
          type="button"
          size="sm"
          variant="danger"
          onMouseDown={() => {
            deleteIntentRowRef.current = info.row.original;
          }}
          onClick={() => handleDeleteRecord(info.row)}
        >
          Delete
        </Button>
      ),
      size: 80,
      enableSorting: false,
    }),
  ];

  const sourceColumns = buildColumns("As they were", "S");
  const destinationColumns = buildColumns("What they became", "D");

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="mergersubs" style={{ paddingLeft: "8px" }}>
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi((prev) => ({ ...prev, showAlert: false }))} dismissible>
        {ui.errorMessage}
      </Alert>

      <Row className="g-2">
        <Col xs={6} sm={6} md={6} lg={6}>
          <Row className="mb-2">
            <Col className="text-right">
              <Button variant="secondary" size="sm" onClick={() => addMergerSubDetailRecord("S")}>Add</Button>
            </Col>
          </Row>

          <GenericTable
            columns={sourceColumns}
            data={sourceRows}
            enablePagination={false}
            meta={{
              updateData: (rowIndex, columnId, value) => updateDataBySource("S", rowIndex, columnId, value),
              handleSave,
              shouldSkipBlurSave: ({ row }) => deleteIntentRowRef.current === row.original,
            }}
          />
        </Col>
        <Col xs={6} sm={6} md={6} lg={6}>
          <Row className="mb-2">
            <Col className="text-right">
              <Button variant="secondary" size="sm" onClick={() => addMergerSubDetailRecord("D")}>Add</Button>
            </Col>
          </Row>

          <GenericTable
            columns={destinationColumns}
            data={destinationRows}
            enablePagination={false}
            meta={{
              updateData: (rowIndex, columnId, value) => updateDataBySource("D", rowIndex, columnId, value),
              handleSave,
              shouldSkipBlurSave: ({ row }) => deleteIntentRowRef.current === row.original,
            }}
          />
        </Col>
      </Row>
    </div>
  );
}

export default MergerSubsDetailList;